import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  addApplication, addInterview, hireCandidate, loadApplications, loadCandidates, loadDirectory, loadHireSources, loadInterviews, loadOffers,
  loadOrganisation, loadRecruitmentStats, loadRequisitions, moveApplication, requestHire, saveOffer, setRequisitionStatus,
  type Application, type Candidate, type Interview, type Offer, type ReqStatus, type Requisition, type Stage,
} from "../data";
import { money } from "../lib/payslip";
import { FileSlot } from "../components/FileSlot";
import type { Department, DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const PIPE: Stage[] = ["applied", "screening", "interview", "offer", "hired"];
const reqTone: Record<ReqStatus, string> = { requested: "warning", open: "info", on_hold: "neutral", filled: "success", cancelled: "neutral" };
const stars = (n: number | null) => (n ? "★".repeat(n) + "☆".repeat(5 - n) : "");

interface Data {
  reqs: Requisition[]; cands: Candidate[]; apps: Application[]; ivs: Interview[]; offers: Offer[];
  people: DirectoryRow[]; depts: Department[]; sources: { code: string; name_en: string; name_ar: string }[];
}

/** Recruitment: roles (managers ask, HR opens), a pipeline per role, interviews, offers and hiring. */
export function Recruitment({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const hr = ws.permissions.has("recruitment.manage");
  const [d, setD] = useState<Data | null>(null);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof loadRecruitmentStats>>>(null);
  const [openReq, setOpenReq] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => {
    const [reqs, cands, apps, ivs, offers, people, org, sources] = await Promise.all([
      loadRequisitions(cid), loadCandidates(cid), loadApplications(cid), loadInterviews(cid), hr ? loadOffers(cid) : Promise.resolve([]),
      loadDirectory(cid).catch(() => []), loadOrganisation(cid).catch(() => ({ departments: [], branches: [] })), loadHireSources()]);
    setD({ reqs, cands, apps, ivs, offers, people, depts: org.departments, sources });
    if (hr) setStats(await loadRecruitmentStats(cid));
  };
  useEffect(() => { void refresh().catch((e) => setMsg({ ok: false, text: e.message })); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="page">{msg ? <p className="alert danger">{msg.text}</p> : <p className="muted">{t("loading")}</p>}</div>;

  const dept = (id: string | null) => { const x = d.depts.find((y) => y.id === id); return x ? pick(x.name, x.name_ar) : ""; };
  const name = (id: string | null) => { const p = d.people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : ""; };
  const count = (req: string, s?: Stage) => d.apps.filter((a) => a.requisition_id === req && (!s || a.stage === s)).length;
  const req = d.reqs.find((r) => r.id === openReq);
  if (req) return <div className="page"><Pipeline ws={ws} d={d} req={req} hr={hr} onBack={() => setOpenReq(null)} onChanged={refresh} onOpen={onOpen} /></div>;

  const active = d.reqs.filter((r) => r.status === "requested" || r.status === "open" || r.status === "on_hold");
  const closed = d.reqs.filter((r) => r.status === "filled" || r.status === "cancelled");
  const row = (r: Requisition) => (
    <tr key={r.id}>
      <td><button type="button" className="link strong" onClick={() => setOpenReq(r.id)}>{pick(r.title, r.title_ar)}</button>
        <br /><span className="muted small-note">{dept(r.department_id)}{r.hiring_manager ? ` · ${name(r.hiring_manager)}` : ""}</span></td>
      <td className="mono">{count(r.id, "hired")}/{r.headcount}</td>
      <td className="hide-sm mono">{count(r.id) - count(r.id, "rejected") - count(r.id, "withdrawn") - count(r.id, "hired")}</td>
      <td><span className={`badge ${reqTone[r.status]}`}>{t(`rqStatus_${r.status}` as StringKey)}</span></td>
    </tr>
  );

  return (
    <div className="page">
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {stats && (
        <div className="kpis">
          <div className="stat"><span className="stat-label">{t("rqOpenPositions")}</span><span className="stat-value">{stats.open_positions}</span><span className="stat-sub">{stats.open_roles} {t("rqRoles")}</span></div>
          <div className="stat"><span className="stat-label">{t("rqInPipeline")}</span><span className="stat-value">{["applied", "screening", "interview", "offer"].reduce((a, s) => a + (stats.by_stage[s] ?? 0), 0)}</span>
            <span className="stat-sub">{stats.by_stage.interview ?? 0} {t("rqAtInterview")} · {stats.by_stage.offer ?? 0} {t("rqAtOffer")}</span></div>
          <div className="stat"><span className="stat-label">{t("rqHires12")}</span><span className="stat-value">{stats.hires_12m}</span>
            {stats.avg_days_to_hire != null && <span className="stat-sub">{t("rqDaysToHire").replace("{n}", String(stats.avg_days_to_hire))}</span>}</div>
          {stats.requested > 0 && <div className="stat"><span className="stat-label">{t("rqWaitingApproval")}</span><span className="stat-value">{stats.requested}</span></div>}
        </div>
      )}
      <section className="card">
        <h2>{t("rqActive")}</h2>
        {active.length === 0 ? <p className="muted">{t("rqNone")}</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t("rqRole")}</th><th>{t("rqHired")}</th><th className="hide-sm">{t("rqInPipeline")}</th><th>{t("status")}</th></tr></thead>
            <tbody>{active.map(row)}</tbody>
          </table></div>
        )}
      </section>
      <RequestForm ws={ws} depts={d.depts} hr={hr} onDone={async (id) => { await refresh(); setOpenReq(id); }} />
      {closed.length > 0 && (
        <details className="card"><summary>{t("rqClosed")} ({closed.length})</summary>
          <div className="table-wrap"><table><tbody>{closed.map(row)}</tbody></table></div>
        </details>
      )}
    </div>
  );
}

function RequestForm({ ws, depts, hr, onDone }: { ws: Workspace; depts: Department[]; hr: boolean; onDone: (id: string) => void }) {
  const { t, pick } = useI18n();
  const [f, setF] = useState({ title: "", dept: "", n: "1", reason: "new", description: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  async function send(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { const id = await requestHire(ws.membership.company_id, f.title, f.dept || null, Number(f.n), f.reason, f.description); setF({ ...f, title: "", description: "" }); onDone(id); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  return (
    <form className="card" onSubmit={send}>
      <h2>{hr ? t("rqNew") : t("rqAsk")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("rqRole")}</span><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></label>
        <label className="field"><span>{t("department")}</span>
          <select value={f.dept} onChange={(e) => setF({ ...f, dept: e.target.value })}><option value="">—</option>{depts.map((x) => <option key={x.id} value={x.id}>{pick(x.name, x.name_ar)}</option>)}</select></label>
        <label className="field"><span>{t("rqHeadcount")}</span><input type="number" min="1" max="100" value={f.n} onChange={(e) => setF({ ...f, n: e.target.value })} /></label>
        <label className="field"><span>{t("rqReason")}</span>
          <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}><option value="new">{t("rqNewRole")}</option><option value="replacement">{t("rqReplacement")}</option></select></label>
      </div>
      <label className="field"><span>{t("rqDescription")}</span><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
      {msg && <p className="alert danger" role="alert">{msg.text}</p>}
      <div className="actions"><button className="btn primary">{hr ? t("rqOpen") : t("sendToHr")}</button></div>
    </form>
  );
}

function Pipeline({ ws, d, req, hr, onBack, onChanged, onOpen }: {
  ws: Workspace; d: Data; req: Requisition; hr: boolean; onBack: () => void; onChanged: () => Promise<void>; onOpen: (id: string) => void;
}) {
  const { t, pick } = useI18n();
  const [sel, setSel] = useState<string | null>(null);
  const [showOut, setShowOut] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const apps = d.apps.filter((a) => a.requisition_id === req.id);
  const cand = (id: string) => d.cands.find((c) => c.id === id);
  const cname = (a: Application) => { const c = cand(a.candidate_id); return c ? pick(`${c.first_name} ${c.last_name}`, c.first_name_ar ? `${c.first_name_ar} ${c.last_name_ar ?? ""}` : null) : "—"; };
  const out = apps.filter((a) => a.stage === "rejected" || a.stage === "withdrawn");
  async function run(fn: () => Promise<unknown>, done?: string) {
    setMsg(null);
    try { await fn(); await onChanged(); if (done) setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const app = apps.find((a) => a.id === sel);

  return (
    <>
      <button type="button" className="link back" onClick={onBack}>← {t("navRecruitment")}</button>
      <section className="card">
        <div className="row spread">
          <h2>{pick(req.title, req.title_ar)} <span className={`badge ${reqTone[req.status]}`}>{t(`rqStatus_${req.status}` as StringKey)}</span></h2>
          {hr && (
            <div className="row">
              {req.status === "requested" && <button type="button" className="btn primary small" onClick={() => run(() => setRequisitionStatus(req.id, "open"), t("rqOpened"))}>{t("rqApproveOpen")}</button>}
              {req.status === "open" && <button type="button" className="btn secondary small" onClick={() => run(() => setRequisitionStatus(req.id, "on_hold"))}>{t("rqHold")}</button>}
              {req.status === "on_hold" && <button type="button" className="btn secondary small" onClick={() => run(() => setRequisitionStatus(req.id, "open"))}>{t("rqReopen")}</button>}
              {(req.status === "open" || req.status === "on_hold" || req.status === "requested") && <button type="button" className="btn secondary small" onClick={() => run(() => setRequisitionStatus(req.id, "cancelled"))}>{t("rqCancel")}</button>}
            </div>
          )}
        </div>
        <p className="muted small-note">{req.headcount} × · {req.reason === "replacement" ? t("rqReplacement") : t("rqNewRole")}
          {req.salary_min || req.salary_max ? ` · ${money(req.salary_min ?? 0)}–${money(req.salary_max ?? 0)}` : ""}{req.description ? ` · ${req.description}` : ""}</p>
        {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      </section>

      <div className="board" role="list">
        {PIPE.map((s) => (
          <section key={s} className="board-col" role="listitem" aria-label={t(`stage_${s}` as StringKey)}>
            <h3>{t(`stage_${s}` as StringKey)} <span className="muted">{apps.filter((a) => a.stage === s).length}</span></h3>
            {apps.filter((a) => a.stage === s).map((a) => (
              <button key={a.id} type="button" className={`board-card${sel === a.id ? " current" : ""}`} onClick={() => setSel(sel === a.id ? null : a.id)}>
                <strong>{cname(a)}</strong>
                <span className="muted small-note">{cand(a.candidate_id)?.current_title ?? ""}</span>
                {a.rating ? <span className="stars" aria-label={`${a.rating}/5`}>{stars(a.rating)}</span> : null}
              </button>
            ))}
          </section>
        ))}
      </div>
      {out.length > 0 && <button type="button" className="link" onClick={() => setShowOut(!showOut)}>{t("rqOut")} ({out.length})</button>}
      {showOut && <ul className="req-list">{out.map((a) => <li key={a.id}><button type="button" className="link" onClick={() => setSel(a.id)}>{cname(a)}</button><span className="muted small-note">{t(`stage_${a.stage}` as StringKey)}{a.reject_reason ? ` · ${a.reject_reason}` : ""}</span></li>)}</ul>}

      {app && <CandidatePanel ws={ws} d={d} app={app} hr={hr} name={cname(app)} onChanged={onChanged} onOpen={onOpen} />}
      {hr && (req.status === "open" || req.status === "on_hold") && <AddCandidate req={req} sources={d.sources} onAdded={async (id) => { await onChanged(); setSel(id); }} />}
    </>
  );
}

function CandidatePanel({ ws, d, app, hr, name, onChanged, onOpen }: {
  ws: Workspace; d: Data; app: Application; hr: boolean; name: string; onChanged: () => Promise<void>; onOpen: (id: string) => void;
}) {
  const { t, pick } = useI18n();
  const c = d.cands.find((x) => x.id === app.candidate_id);
  const ivs = d.ivs.filter((i) => i.application_id === app.id);
  const offer = d.offers.find((o) => o.application_id === app.id);
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [iv, setIv] = useState({ at: local, kind: "onsite", who: ws.membership.display_name ?? "", score: "", rec: "", notes: "" });
  const [of, setOf] = useState({ basic: String(offer?.basic_salary ?? ""), allow: String(offer?.allowances ?? "0"), start: offer?.start_date ?? "" });
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { setOf({ basic: String(offer?.basic_salary ?? ""), allow: String(offer?.allowances ?? "0"), start: offer?.start_date ?? "" }); setMsg(null); }, [app.id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function run(fn: () => Promise<unknown>, done?: string) {
    setMsg(null);
    try { await fn(); await onChanged(); if (done) setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const src = d.sources.find((s) => s.code === c?.source_code);
  const live = app.stage !== "hired" && app.stage !== "rejected" && app.stage !== "withdrawn";

  return (
    <section className="card">
      <div className="row spread">
        <h2>{name} <span className="badge info">{t(`stage_${app.stage}` as StringKey)}</span></h2>
        {app.employment_id && <button type="button" className="btn secondary small" onClick={() => onOpen(app.employment_id!)}>{t("rqOpenEmployee")}</button>}
      </div>
      <p className="muted small-note">
        {c?.current_title ? `${c.current_title} · ` : ""}{c?.email ?? ""}{c?.mobile ? ` · ${c.mobile}` : ""}{src ? ` · ${pick(src.name_en, src.name_ar)}` : ""}
        {c?.cv_link ? <> · <a href={c.cv_link} target="_blank" rel="noreferrer">{t("rqCv")}</a></> : null}
      </p>
      {c && (hr || c.cv_path) && <p className="small-note"><strong>{t("rqCvFile")}:</strong> <FileSlot companyId={ws.membership.company_id} area="cv" ownerId={c.id} recordId={c.id} path={c.cv_path} canEdit={hr} onChanged={onChanged} /></p>}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {hr && live && (
        <div className="row">
          <label className="field compact"><span>{t("rqMoveTo")}</span>
            <select value={app.stage} onChange={(e) => void run(() => moveApplication(app.id, e.target.value as Stage))}>
              {(["applied", "screening", "interview", "offer"] as Stage[]).map((s) => <option key={s} value={s}>{t(`stage_${s}` as StringKey)}</option>)}
            </select></label>
          <label className="field compact"><span>{t("rqRating")}</span>
            <select value={app.rating ?? ""} onChange={(e) => void run(() => moveApplication(app.id, app.stage, undefined, Number(e.target.value) || null))}>
              <option value="">—</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{stars(n)}</option>)}
            </select></label>
          <input placeholder={t("rqRejectReason")} aria-label={t("rqRejectReason")} value={reason} onChange={(e) => setReason(e.target.value)} />
          <button type="button" className="btn secondary small" onClick={() => run(() => moveApplication(app.id, "rejected", reason), t("rqRejected"))}>{t("reject")}</button>
          <button type="button" className="btn secondary small" onClick={() => run(() => moveApplication(app.id, "withdrawn", reason))}>{t("rqWithdrawn")}</button>
        </div>
      )}

      <h3 className="sub-head">{t("rqInterviews")}</h3>
      {ivs.length === 0 ? <p className="muted small-note">{t("rqNoInterviews")}</p> : (
        <ul className="req-list">
          {ivs.map((i) => (
            <li key={i.id}><span><strong>{t(`ivKind_${i.kind}` as StringKey)}</strong> · <span className="mono">{i.scheduled_at.slice(0, 16).replace("T", " ")}</span>{i.interviewer ? ` · ${i.interviewer}` : ""}
              {i.notes ? <span className="muted small-note"> · {i.notes}</span> : null}</span>
              <span>{i.score ? <span className="stars">{stars(i.score)}</span> : <span className="badge neutral">{t("rqUpcoming")}</span>} {i.recommendation && <span className={`badge ${i.recommendation === "hire" ? "success" : i.recommendation === "no" ? "danger" : "warning"}`}>{t(`ivRec_${i.recommendation}` as StringKey)}</span>}</span></li>
          ))}
        </ul>
      )}
      {live && (
        <form className="row" onSubmit={(e) => { e.preventDefault(); void run(() => addInterview(app.id, new Date(iv.at).toISOString(), iv.kind, iv.who, iv.score ? Number(iv.score) : null, iv.rec || null, iv.notes), t("saved")); }}>
          <input type="datetime-local" aria-label={t("date")} value={iv.at} onChange={(e) => setIv({ ...iv, at: e.target.value })} required />
          <select aria-label={t("otKind")} value={iv.kind} onChange={(e) => setIv({ ...iv, kind: e.target.value })}>{["phone", "video", "onsite", "technical", "hr"].map((k) => <option key={k} value={k}>{t(`ivKind_${k}` as StringKey)}</option>)}</select>
          <input placeholder={t("rqInterviewer")} aria-label={t("rqInterviewer")} value={iv.who} onChange={(e) => setIv({ ...iv, who: e.target.value })} />
          <select aria-label={t("rqScore")} value={iv.score} onChange={(e) => setIv({ ...iv, score: e.target.value })}><option value="">{t("rqScore")}</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{stars(n)}</option>)}</select>
          <select aria-label={t("rqRecommend")} value={iv.rec} onChange={(e) => setIv({ ...iv, rec: e.target.value })}><option value="">{t("rqRecommend")}</option>{["hire", "maybe", "no"].map((r) => <option key={r} value={r}>{t(`ivRec_${r}` as StringKey)}</option>)}</select>
          <input placeholder={t("note")} aria-label={t("note")} value={iv.notes} onChange={(e) => setIv({ ...iv, notes: e.target.value })} />
          <button className="btn secondary small">{t("payAdd")}</button>
        </form>
      )}

      {hr && live && (
        <>
          <h3 className="sub-head">{t("rqOffer")}{offer ? ` · ${t(`ofStatus_${offer.status}` as StringKey)}` : ""}</h3>
          <form className="row" onSubmit={(e) => { e.preventDefault(); void run(() => saveOffer(app.id, Number(of.basic), Number(of.allow || 0), of.start, "sent"), t("rqOfferSent")); }}>
            <label className="field compact"><span>{t("payBasic")}</span><input type="number" min="1" step="0.01" value={of.basic} onChange={(e) => setOf({ ...of, basic: e.target.value })} required /></label>
            <label className="field compact"><span>{t("payAllowances")}</span><input type="number" min="0" step="0.01" value={of.allow} onChange={(e) => setOf({ ...of, allow: e.target.value })} /></label>
            <label className="field compact"><span>{t("rqStart")}</span><input type="date" value={of.start} onChange={(e) => setOf({ ...of, start: e.target.value })} required /></label>
            <button className="btn secondary small">{offer ? t("rqUpdateOffer") : t("rqSendOffer")}</button>
          </form>
          {offer && (
            <div className="row">
              <button type="button" className="btn primary" onClick={() => { if (window.confirm(t("rqHireAsk"))) void run(() => hireCandidate(app.id, null), t("rqHiredMsg")); }}>{t("rqHire")}</button>
              <button type="button" className="btn secondary small" onClick={() => run(() => saveOffer(app.id, offer.basic_salary, offer.allowances, offer.start_date, "declined"))}>{t("rqDeclined")}</button>
            </div>
          )}
          <p className="muted small-note">{t("rqHireHelp")}</p>
        </>
      )}
    </section>
  );
}

function AddCandidate({ req, sources, onAdded }: { req: Requisition; sources: { code: string; name_en: string; name_ar: string }[]; onAdded: (id: string) => void }) {
  const { t, pick } = useI18n();
  const empty = { first_name: "", last_name: "", email: "", mobile: "", source: "", current_title: "", cv_link: "" };
  const [f, setF] = useState(empty);
  const [msg, setMsg] = useState<string | null>(null);
  async function add(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { const id = await addApplication(req.id, null, f); setF(empty); onAdded(id); }
    catch (err) { setMsg(errText(err, t("errorGeneric"))); }
  }
  const input = (k: keyof typeof empty, label: StringKey, type = "text", required = false) => (
    <label className="field"><span>{t(label)}</span><input type={type} value={f[k]} required={required} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
  );
  return (
    <form className="card" onSubmit={add}>
      <h2>{t("rqAddCandidate")}</h2>
      <div className="field-row">{input("first_name", "firstName", "text", true)}{input("last_name", "lastName", "text", true)}{input("current_title", "rqCurrentTitle")}</div>
      <div className="field-row">
        {input("email", "email", "email")}{input("mobile", "mobile", "tel")}
        <label className="field"><span>{t("rqSource")}</span>
          <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}><option value="">—</option>{sources.map((s) => <option key={s.code} value={s.code}>{pick(s.name_en, s.name_ar)}</option>)}</select></label>
        {input("cv_link", "rqCvLink", "url")}
      </div>
      {msg && <p className="alert danger" role="alert">{msg}</p>}
      <div className="actions"><button className="btn primary">{t("payAdd")}</button></div>
    </form>
  );
}
